import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type KeyboardEvent, type ReactNode, useRef } from "react";
import { FONT_SIZE, INK, LETTER_SPACING, MOTION, NEUTRAL, SIDE_BY_SIDE_COMPARE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { ColorDot } from "../molecules/ColorDot";
import { CompareSideHeader } from "./CompareSideHeader";

export type CompareSideId = string | number;

/** One thing being compared — a record, a version, a plan. Every string is already translated. */
export type CompareSide<TId extends CompareSideId> = {
	id: TId;
	/** Headline, and the radio's accessible name when the comparison is selectable. */
	title: string;
	subtitle?: ReactNode;
	/** Annotations under the title — e.g. "Kept" / "Will be deleted" chips that follow the selection. */
	badge?: ReactNode;
	dataTestId?: string;
};

/** One compared field. `values` is keyed by side id and must have an entry for every side. */
export type CompareRow<TId extends CompareSideId> = {
	id: string;
	label: string;
	values: Record<TId, ReactNode>;
	/**
	 * Plain-text form of each side's value, used only to decide whether the row differs. Omit for a row that
	 * should never be marked (a field that is expected to differ, like an id).
	 */
	compareValues?: Record<TId, string>;
	dataTestId?: string;
};

type Props<TId extends CompareSideId> = {
	sides: readonly CompareSide<TId>[];
	rows: readonly CompareRow<TId>[];
	/** Makes the headers a radio group: the chosen side is drawn as a lane down the whole comparison. */
	selectedSideId?: TId;
	onSelectSide?: (id: TId) => void;
	/** Accessible name of the radio group ("Record to keep"). Required in practice when selectable. */
	selectionAriaLabel?: string;
	/** Accessible name of the comparison table ("Compare the two records"). */
	ariaLabel: string;
	/** Accessible text and hover hint of the marker on a row whose values differ ("Different"). */
	differenceLabel: string;
	/** Optional line naming how much differs — receives the count of differing rows. Sits in the corner cell. */
	renderDifferenceSummary?: (differingCount: number) => ReactNode;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const rowDiffers = <TId extends CompareSideId>(row: CompareRow<TId>, sides: readonly CompareSide<TId>[]): boolean => {
	if (!row.compareValues) return false;
	const normalized = sides.map((side) => row.compareValues?.[side.id]?.trim().toLowerCase());
	return new Set(normalized).size > 1;
};

/**
 * **Two (or more) records side by side, with what differs marked** — the comparison you make before
 * merging, replacing or choosing between versions of one thing. Rows are fields; columns are the sides. A
 * row whose values disagree gets a status marker and full ink, and the rows that agree recede, so the eye
 * lands on the differences.
 *
 * Optionally a choice: pass `selectedSideId` + `onSelectSide` and each side's header becomes a radio
 * (WAI-ARIA radio group — one tab stop, arrow keys move and select); the chosen side is washed as one
 * continuous lane from its header to the last row, crossfading when the choice moves.
 *
 * Generic and copy-free: the caller owns every label, value node and badge. Stacks on phones — the field
 * label moves above its values, which stay side by side. All colour resolves from the theme palette.
 */
export const SideBySideCompare = <TId extends CompareSideId>({
	sides,
	rows,
	selectedSideId,
	onSelectSide,
	selectionAriaLabel,
	ariaLabel,
	differenceLabel,
	renderDifferenceSummary,
	dataTestId,
	sx,
}: Props<TId>) => {
	const selectable = onSelectSide !== undefined;
	const headerRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const differing = rows.map((row) => rowDiffers(row, sides));
	const differingCount = differing.filter(Boolean).length;
	const tabStopIndex = Math.max(
		0,
		sides.findIndex((side) => side.id === selectedSideId),
	);

	const columns = `repeat(${sides.length}, minmax(0, 1fr))`;
	const gridSx: SxProps<Theme> = {
		display: "grid",
		gridTemplateColumns: { xs: columns, sm: `${SIDE_BY_SIDE_COMPARE.LABEL_COLUMN_WIDTH}px ${columns}` },
		columnGap: 1,
	};

	// Arrow keys move the choice, as a native radio group does; reading direction flips left/right.
	const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLElement>) => {
		const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
		const forward = event.key === "ArrowDown" || event.key === (rtl ? "ArrowLeft" : "ArrowRight");
		const backward = event.key === "ArrowUp" || event.key === (rtl ? "ArrowRight" : "ArrowLeft");
		if (!forward && !backward) return;
		event.preventDefault();
		const next = (index + (forward ? 1 : -1) + sides.length) % sides.length;
		onSelectSide?.(sides[next].id);
		headerRefs.current[next]?.focus();
	};

	const laneSx = (sideId: TId, isLastRow: boolean) => (theme: Theme) => ({
		backgroundColor: sideId === selectedSideId ? alpha(theme.palette.primary.main, SIDE_BY_SIDE_COMPARE.LANE_WASH_ALPHA) : "transparent",
		borderRadius: isLastRow ? `0 0 ${SIDE_BY_SIDE_COMPARE.LANE_RADIUS_PX}px ${SIDE_BY_SIDE_COMPARE.LANE_RADIUS_PX}px` : 0,
		transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
		"@media (prefers-reduced-motion: reduce)": { transition: "none" },
	});

	return (
		<Box data-testid={dataTestId} sx={[{ display: "flex", flexDirection: "column", width: "100%" }, ...(Array.isArray(sx) ? sx : [sx])]}>
			<Box sx={gridSx}>
				<Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "flex-end", pb: 1.25, pr: 1 }}>
					{renderDifferenceSummary && (
						<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.35 }}>
							{renderDifferenceSummary(differingCount)}
						</Typography>
					)}
				</Box>
				<Box
					role={selectable ? "radiogroup" : undefined}
					aria-label={selectable ? selectionAriaLabel : undefined}
					sx={{ display: "grid", gridTemplateColumns: columns, columnGap: 1, gridColumn: { xs: "1 / -1", sm: "2 / -1" } }}
				>
					{sides.map((side, index) => (
						<CompareSideHeader
							key={side.id}
							title={side.title}
							subtitle={side.subtitle}
							badge={side.badge}
							selected={selectable ? side.id === selectedSideId : undefined}
							onSelect={selectable ? () => onSelectSide(side.id) : undefined}
							onKeyDown={selectable ? handleKeyDown(index) : undefined}
							tabbable={index === tabStopIndex}
							buttonRef={(node) => {
								headerRefs.current[index] = node;
							}}
							dataTestId={side.dataTestId}
						/>
					))}
				</Box>
			</Box>
			<Box role="table" aria-label={ariaLabel} sx={gridSx}>
				{rows.map((row, rowIndex) => {
					const isLastRow = rowIndex === rows.length - 1;
					const differs = differing[rowIndex];
					return (
						<Box key={row.id} role="row" data-testid={row.dataTestId} data-differs={differs ? "true" : undefined} sx={{ display: "contents" }}>
							<Box
								role="rowheader"
								sx={{
									gridColumn: { xs: "1 / -1", sm: "auto" },
									display: "flex",
									alignItems: { xs: "center", sm: "flex-start" },
									gap: 0.75,
									pt: 1.25,
									pb: { xs: 0.25, sm: 1.25 },
									borderTop: { xs: "none", sm: rowIndex === 0 ? "none" : `1px solid ${NEUTRAL[150]}` },
								}}
							>
								<Typography
									component="span"
									sx={{
										fontSize: FONT_SIZE.LABEL_XS,
										fontWeight: 700,
										letterSpacing: LETTER_SPACING.LABEL,
										textTransform: "uppercase",
										lineHeight: 1.6,
										color: differs ? INK.PRIMARY : INK.MUTED,
									}}
								>
									{row.label}
								</Typography>
								{differs && (
									<InHouseTooltip title={differenceLabel} placement="top">
										<Box component="span" sx={{ display: "inline-flex", alignSelf: "center" }}>
											<ColorDot color="warning.main" ariaLabel={differenceLabel} />
										</Box>
									</InHouseTooltip>
								)}
							</Box>
							{sides.map((side) => (
								<Box
									key={side.id}
									role="cell"
									sx={[
										laneSx(side.id, isLastRow),
										{
											minHeight: SIDE_BY_SIDE_COMPARE.ROW_MIN_HEIGHT,
											px: 1.5,
											py: 1.25,
											boxSizing: "border-box",
											display: "flex",
											alignItems: "flex-start",
											minWidth: 0,
											fontSize: FONT_SIZE.EMPHASIS,
											lineHeight: 1.4,
											overflowWrap: "anywhere",
											color: differs ? INK.PRIMARY : INK.SECONDARY,
											fontWeight: differs ? 600 : 400,
											borderTop: { xs: "none", sm: rowIndex === 0 ? "none" : `1px solid ${NEUTRAL[150]}` },
										},
									]}
								>
									{row.values[side.id]}
								</Box>
							))}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

export default SideBySideCompare;
